import { setOptions, importLibrary } from "@googlemaps/js-api-loader";

/**
 * Gemeinsamer Lader für die Google-Maps-JS-API. Bisher lud nur
 * `useGooglePlaces` die Places-Library; seit der 3D-Schrägansicht kommt
 * `geocoding` dazu. Beide teilen sich dasselbe Script, deshalb liegt die
 * Initialisierung hier an einer Stelle:
 *
 * - `setOptions` wird höchstens EINMAL aufgerufen (die Maps-JS-API lädt genau
 *   ein Script mit genau einem Key — mehrfaches Setzen hilft nicht),
 * - je Library gibt es genau EIN Promise (Map), auch bei vielen Komponenten,
 * - ohne Key wird `null` zurückgegeben und einmalig gewarnt; die Aufrufer
 *   verhalten sich dann exakt wie vorher (Plain-Input bzw. 2D-Such-Link).
 *
 * Der Key kommt aus `VITE_GOOGLE_MAPS_AUTOCOMPLETE` — eine zweite Variable nur
 * für die Geokodierung gibt es bewusst nicht, siehe unten.
 *
 * Lose typisiert (`any`) — `@types/google.maps` ist nicht installiert.
 */

export type GoogleLibraryName = "places" | "geocoding";

/**
 * EIN Key für alles: `VITE_GOOGLE_MAPS_AUTOCOMPLETE` deckt sowohl das
 * Adress-Autocomplete (Places) als auch die Geokodierung ab. Ein separater
 * Geokodierungs-Key ist technisch nicht möglich — die Maps-JS-API lädt genau
 * ein Script mit genau einem Key pro Seite. Voraussetzung im Google-Cloud-
 * Projekt: „Maps JavaScript API" UND „Geocoding API" aktiviert.
 */
export const GOOGLE_MAPS_KEY: string | undefined = import.meta.env
  .VITE_GOOGLE_MAPS_AUTOCOMPLETE as string | undefined;

/** Für Aufrufer, die ihr UI am Vorhandensein eines Keys ausrichten. */
export const hasGoogleMapsKey = (): boolean => !!GOOGLE_MAPS_KEY;

let optionsGesetzt = false;
const bibliotheken = new Map<GoogleLibraryName, Promise<any>>();
const gewarnt = new Set<string>();

function warnEinmal(schluessel: string, nachricht: string): void {
  if (gewarnt.has(schluessel)) return;
  gewarnt.add(schluessel);
  console.warn(nachricht);
}

/**
 * Lädt eine Maps-Library und liefert das Promise darauf — oder `null`, wenn
 * kein Key gesetzt ist. Das Promise löst bei Ladefehlern mit `null` auf
 * (Key ungültig, API im Google-Cloud-Projekt nicht aktiviert, Netzfehler),
 * es lehnt nie ab.
 */
export function ensureGoogleLibrary(lib: GoogleLibraryName): Promise<any> | null {
  if (!GOOGLE_MAPS_KEY) {
    if (lib === "places") {
      // DEBUG: ohne Key bleibt das Feld ein Plain-Input — häufigste Ursache, wenn
      // das Autocomplete „nichts tut". VITE_GOOGLE_MAPS_AUTOCOMPLETE im Build prüfen.
      warnEinmal(
        "kein-key-places",
        "[GMaps] VITE_GOOGLE_MAPS_AUTOCOMPLETE fehlt → Adress-Autocomplete deaktiviert (Plain-Input).",
      );
    } else {
      warnEinmal(
        "kein-key-geocoding",
        "[GMaps] VITE_GOOGLE_MAPS_AUTOCOMPLETE fehlt → keine Geokodierung; der " +
          "Google-Earth-Link bleibt bei der Adresssuche ohne 3D-Kamera.",
      );
    }
    return null;
  }

  const vorhanden = bibliotheken.get(lib);
  if (vorhanden) return vorhanden;

  if (!optionsGesetzt) {
    setOptions({ key: GOOGLE_MAPS_KEY, v: "weekly" });
    optionsGesetzt = true;
  }

  console.log(`[GMaps] Key vorhanden (len=${GOOGLE_MAPS_KEY.length}); importLibrary('${lib}') wird geladen …`);
  const promise = (importLibrary(lib) as Promise<any>)
    .then((geladen) => {
      console.log(`[GMaps] importLibrary('${lib}') erfolgreich geladen`, geladen);
      return geladen;
    })
    .catch((err) => {
      // Bisher still verschluckt — hier loggen, damit Key-/API-/Netzwerkfehler
      // sichtbar werden (z. B. „Geocoding API" im Projekt nicht aktiviert).
      console.error(`[GMaps] importLibrary('${lib}') FEHLGESCHLAGEN:`, err);
      return null;
    });

  bibliotheken.set(lib, promise);
  return promise;
}
